import { Card } from '@/components/ui/card';
import type { PreviewInvoice } from '@/types/firebase';

interface InvoiceEmailPreviewProps {
  template: string;
  invoice: PreviewInvoice;
  companyName: string;
}

export function InvoiceEmailPreview({ template, invoice, companyName }: InvoiceEmailPreviewProps) {
  // Helper function to safely format dates from various sources (timezone-safe)
  const formatDate = (dateValue: any): string => {
    if (!dateValue) return '';
    
    try {
      let date;
      // Handle Firestore Timestamp objects
      if (dateValue && typeof dateValue === 'object' && 'seconds' in dateValue) {
        date = new Date(dateValue.seconds * 1000);
      } else if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        // Handle YYYY-MM-DD format specifically to avoid timezone issues
        const [year, month, day] = dateValue.split('-').map(Number);
        date = new Date(year, month - 1, day); // month is 0-indexed
      } else {
        // Handle standard Date objects or ISO strings
        date = new Date(dateValue);
      }
      
      // Get local date components without timezone conversion
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      
      // Create a new date using local components to avoid timezone shift
      const localDate = new Date(year, month, day);
      
      // Use locale-specific date format to display the date
      return localDate.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Format customer address to include zip code
  const formatCustomerAddress = (address: string | undefined): string => {
    if (!address) {
      // If we have no customerAddress, return empty string
      return '';
    }
    
    // Format the address as it is
    return address;
  };

  // Replace template variables with actual values without brackets
  const processedTemplate = template
    .replace(/\{\{companyName\}\}/g, companyName || 'Your Company Name')
    .replace(/\{\{customerName\}\}/g, invoice.customerName || 'John Smith')
    .replace(/\{\{customerAddress\}\}/g, formatCustomerAddress(invoice.customerAddress))
    .replace(/\{\{invoiceNumber\}\}/g, invoice.invoiceNumber || 'INV-001')
    .replace(/\{\{invoiceAmount\}\}/g, invoice.total ? `$${invoice.total.toFixed(2)}` : '$0.00')
    .replace(/\{\{dueDate\}\}/g, formatDate(invoice.dueDate))
    .replace(/\{\{paymentDate\}\}/g, 'March 1, 2024')
    .replace(/\{\{refundAmount\}\}/g, '$500.00')
    .replace(/\{\{refundDate\}\}/g, 'March 2, 2024')
    .replace(/\{\{failedDate\}\}/g, 'March 1, 2024')
    .replace(/\{\{failureReason\}\}/g, 'Insufficient funds')
    .replace(/\{\{paymentLink\}\}/g, '#payment-link')
    .replace(/\{\{frequency\}\}/g, invoice.recurringDetails?.frequency ? 
      (invoice.recurringDetails.frequency === 'weekly' ? 'Weekly' : 
       invoice.recurringDetails.frequency === 'monthly' ? 'Monthly' : 
       invoice.recurringDetails.frequency === 'quarterly' ? 'Quarterly' : 'Annual') : 'Monthly');

  // Only render if there's actual content
  if (!processedTemplate.trim()) {
    return null;
  }

  return (
    <Card className="p-4 bg-white">
      <div 
        className="prose max-w-none [&_p]:my-0 [&_p]:min-h-[1.5em] [&_p]:whitespace-pre-wrap"
        dangerouslySetInnerHTML={{ __html: processedTemplate }}
      />
    </Card>
  );
}